import { useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { BOOKING_STATUS_META } from "../../lib/status";

const AVAILABILITY_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "in_use", label: "In Use" },
  { value: "maintenance", label: "Maintenance" },
  { value: "needs_inspection", label: "Needs Inspection" },
  { value: "unavailable", label: "Unavailable" },
];

const BOOKING_STATUS_OPTIONS = ["awaiting_staff_approval", "awaiting_customer_confirmation", "confirmed", "ongoing"]
  .map((v) => ({ value: v, label: BOOKING_STATUS_META[v].label }));

function toggle(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function ChipGroup({ title, options, selected, onChange, testIdPrefix }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-1.5">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              data-testid={`${testIdPrefix}-${o.value}`}
              onClick={() => onChange(toggle(selected, o.value))}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                active ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilterControls({ search, setSearch, category, setCategory, availabilityState, setAvailabilityState,
                          bookingStatus, setBookingStatus, categoryOptions }) {
  return (
    <div className="space-y-4">
      <label className="relative block">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, plate, category…"
          data-testid="fc-search"
          className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F] text-sm w-full"
        />
      </label>
      {categoryOptions.length > 0 && (
        <ChipGroup title="Category" options={categoryOptions.map((c) => ({ value: c, label: c }))}
                   selected={category} onChange={setCategory} testIdPrefix="fc-filter-category" />
      )}
      <ChipGroup title="Availability" options={AVAILABILITY_OPTIONS} selected={availabilityState}
                 onChange={setAvailabilityState} testIdPrefix="fc-filter-availability" />
      <ChipGroup title="Booking status" options={BOOKING_STATUS_OPTIONS} selected={bookingStatus}
                 onChange={setBookingStatus} testIdPrefix="fc-filter-booking-status" />
    </div>
  );
}

/** Filters + search for the Fleet Calendar. Every option here maps directly
 * to a /fleet-calendar/schedule query param: no client-side reimplementation
 * of availability logic, the server does the actual filtering. */
export function FilterBar(props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const activeCount = props.category.length + props.availabilityState.length + props.bookingStatus.length;

  return (
    <>
      {/* Desktop: inline panel */}
      <div className="hidden md:block rounded-xl border border-slate-200 bg-white p-4" data-testid="fc-filter-panel">
        <FilterControls {...props} />
      </div>

      {/* Mobile: search bar + button that opens a bottom sheet */}
      <div className="md:hidden flex items-center gap-2">
        <label className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={props.search} onChange={(e) => props.setSearch(e.target.value)}
            placeholder="Search vehicles…" data-testid="fc-search-mobile"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F] text-sm w-full"
          />
        </label>
        <button type="button" onClick={() => setDrawerOpen(true)} data-testid="fc-filter-drawer-open"
                className="relative px-3 py-2 rounded-lg border border-slate-300 bg-white">
          <SlidersHorizontal className="w-4 h-4" />
          {activeCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#1B4332] text-white text-[9px] flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {drawerOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setDrawerOpen(false)} data-testid="fc-filter-drawer-backdrop" />
          <div className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white border-t border-slate-200 p-5"
               data-testid="fc-filter-drawer">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-semibold">Filters</h3>
              <button onClick={() => setDrawerOpen(false)} data-testid="fc-filter-drawer-close" className="p-1.5 rounded-md hover:bg-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <FilterControls {...props} />
            <button onClick={() => setDrawerOpen(false)} data-testid="fc-filter-drawer-apply"
                    className="mt-5 w-full px-4 py-2.5 rounded-lg bg-[#1B4332] text-white text-sm font-medium">
              Show results
            </button>
          </div>
        </>
      )}
    </>
  );
}
